import { useId, useMemo, useState } from 'react'
import type { GrowthPoint, GrowthSeries } from '../../../shared/api/platform'

/**
 * F1's "User Growth Overview", drawn from data.
 *
 * 🔴 WHAT THIS REPLACED. The card was three hardcoded `<path d="M 10,130 Q …">`
 * curves, eighteen hardcoded `<circle>` points and five hardcoded axis labels
 * reading "May 20 · May 27 · Jun 3 · Jun 10 · Jun 17". It rendered the same
 * rising line whether the platform had one school or none, whether the API
 * answered or failed, and its axis stayed in May–June however long the product
 * ran. On a tier where an operator decides things from what is on screen, an
 * invented curve is worse than an empty card, because it is convincing.
 *
 * 🔴 A LINE CHART IS THE RIGHT SHAPE HERE AND THE DATA NOW SUPPORTS IT.
 * `Player.createdAt` and `User.createdAt` (role-indexed) give all three series
 * the legend promises; the question was never the chart type, it was that
 * nothing had been wired to it.
 *
 * ⚠️ THE Y AXIS IS NOT ZERO-BASED WHEN THE VALUES ARE FAR FROM ZERO, AND IT SAYS
 * SO BY PRINTING BOTH BOUNDS. A cumulative curve over a month moves by a few per
 * cent of its own height; forced to a zero baseline it is three flat lines. The
 * honest fix is to label the floor rather than to hide it — which is why the
 * lowest gridline carries its value instead of being decorative.
 */

export interface GrowthChartProps {
  series: GrowthSeries
  /** Which lines to draw. Toggled by the legend; never empty (the last one cannot be turned off). */
  visible?: readonly SeriesKey[]
}

export type SeriesKey = 'students' | 'teachers' | 'parents'

interface SeriesSpec {
  key: SeriesKey
  label: string
  /** Hex rather than a token, because SVG `stroke` cannot read a CSS variable through `currentColor` here. */
  stroke: string
  dot: string
}

/* The three the legend has always named. Order is the order they are stacked in
   the legend and in the tooltip, largest population first. */
export const GROWTH_SERIES: readonly SeriesSpec[] = [
  { key: 'students', label: 'Students', stroke: '#22C55E', dot: 'bg-emerald-500' },
  { key: 'teachers', label: 'Teachers', stroke: '#3B82F6', dot: 'bg-blue-500' },
  { key: 'parents', label: 'Parents', stroke: '#A855F7', dot: 'bg-purple-500' },
]

const W = 400
const H = 180
const PAD = { top: 12, right: 8, bottom: 8, left: 8 }

/**
 * The drawing bounds.
 *
 * 🔴 COMPUTED OVER THE VISIBLE SERIES ONLY. A parents line an operator has
 * switched off must not keep the axis stretched to accommodate it — the chart
 * would answer a question nobody is asking and flatten the two that are.
 *
 * ⚠️ A FLAT SERIES GETS A BAND RATHER THAN A DIVISION BY ZERO. When every value
 * is identical the range is 0, and the line is drawn down the middle: true, and
 * visibly flat, which is the honest rendering of a platform that did not grow.
 */
export function bounds(
  points: readonly GrowthPoint[],
  visible: readonly SeriesKey[],
): { min: number; max: number } {
  const values = points.flatMap((p) => visible.map((k) => p[k]))
  if (values.length === 0) return { min: 0, max: 1 }
  const min = Math.min(...values)
  const max = Math.max(...values)
  return max === min ? { min: Math.max(0, min - 1), max: max + 1 } : { min, max }
}

/** One series as an SVG polyline. Straight segments — a smoothed curve would invent values between days. */
export function pathFor(
  points: readonly GrowthPoint[],
  key: SeriesKey,
  b: { min: number; max: number },
): string {
  if (points.length === 0) return ''
  const span = b.max - b.min || 1
  const plotW = W - PAD.left - PAD.right
  const plotH = H - PAD.top - PAD.bottom
  const step = points.length === 1 ? 0 : plotW / (points.length - 1)
  return points
    .map((p, i) => {
      const x = PAD.left + step * i
      const y = PAD.top + plotH - ((p[key] - b.min) / span) * plotH
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

/**
 * The handful of dates printed under the axis.
 *
 * ⚠️ AT MOST FIVE, EVENLY SPACED, AND ALWAYS INCLUDING BOTH ENDS. Thirty labels
 * under a 400px card is a grey smear; two is a chart whose middle is unreadable.
 */
export function axisTicks(points: readonly GrowthPoint[], count = 5): GrowthPoint[] {
  if (points.length <= count) return [...points]
  const step = (points.length - 1) / (count - 1)
  return Array.from({ length: count }, (_, i) => points[Math.round(step * i)]).filter(
    (p): p is GrowthPoint => p !== undefined,
  )
}

/** `2026-08-22` → `22 Aug`. The year is in the caption; repeating it five times is noise. */
export function tickLabel(day: string): string {
  const at = new Date(`${day}T00:00:00.000Z`)
  return Number.isNaN(at.getTime())
    ? day
    : at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

export function GrowthChart({ series, visible }: GrowthChartProps) {
  const [off, setOff] = useState<readonly SeriesKey[]>([])
  const titleId = useId()

  const shown = useMemo<readonly SeriesKey[]>(() => {
    const base = visible ?? GROWTH_SERIES.map((s) => s.key)
    const next = base.filter((k) => !off.includes(k))
    /* ⚠️ NEVER EMPTY. An operator who switched every line off would be looking at
       an axis and wondering whether the read had failed. */
    return next.length === 0 ? base : next
  }, [visible, off])

  const points = series.points
  const b = useMemo(() => bounds(points, shown), [points, shown])
  const ticks = useMemo(() => axisTicks(points), [points])
  const last = points[points.length - 1]

  /*
   * 🔴 THE EMPTY CASE IS A SENTENCE, NOT A FLAT LINE AT ZERO. A chart with no
   * points and axes still drawn reads as "the platform has nobody", which is a
   * measurement claim; "nothing recorded" is the truth when the read came back
   * with no days in it.
   */
  if (points.length === 0) {
    return (
      <p className="flex h-56 items-center justify-center text-center font-dash text-dash-xs font-semibold text-muted">
        Nothing recorded for this period yet.
      </p>
    )
  }

  return (
    <>
      {/* The legend is the control. Clicking a series hides its line. */}
      <div className="mb-4 flex flex-wrap items-center gap-4 text-dash-xs font-semibold">
        {GROWTH_SERIES.map((s) => {
          const on = shown.includes(s.key)
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={on}
              onClick={() => {
                setOff((prev) =>
                  prev.includes(s.key) ? prev.filter((k) => k !== s.key) : [...prev, s.key],
                )
              }}
              className={`flex cursor-pointer items-center gap-1.5 rounded-pill px-1.5 py-0.5 transition-opacity ${on ? '' : 'opacity-40'}`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
              <span className="text-muted">{s.label}</span>
              <span className="tabular font-bold text-ink">
                {(last?.[s.key] ?? 0).toLocaleString()}
              </span>
            </button>
          )
        })}
      </div>

      <div className="relative h-56 w-full pt-2">
        <svg
          className="h-full w-full overflow-visible"
          viewBox={`0 0 ${String(W)} ${String(H)}`}
          role="img"
          aria-labelledby={titleId}
        >
          <title id={titleId}>
            {`Cumulative users over ${String(series.windowDays)} days. ` +
              GROWTH_SERIES.filter((s) => shown.includes(s.key))
                .map((s) => `${s.label}: ${String(last?.[s.key] ?? 0)}`)
                .join(', ')}
          </title>

          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={f}
              x1="0"
              x2={W}
              y1={PAD.top + (H - PAD.top - PAD.bottom) * f}
              y2={PAD.top + (H - PAD.top - PAD.bottom) * f}
              stroke="var(--color-line)"
              strokeWidth="1"
            />
          ))}

          {GROWTH_SERIES.filter((s) => shown.includes(s.key)).map((s) => (
            <path
              key={s.key}
              d={pathFor(points, s.key, b)}
              fill="none"
              stroke={s.stroke}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>

        {/*
          🔴 BOTH BOUNDS PRINTED, BECAUSE THE AXIS DOES NOT START AT ZERO. Without
          these two numbers the same curve reads as explosive growth or as a flat
          month depending on nothing the reader can see.
        */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex flex-col justify-between py-2 text-[10px] font-bold tabular text-muted">
          <span>{b.max.toLocaleString()}</span>
          <span>{b.min.toLocaleString()}</span>
        </div>

        <div className="mt-2 flex justify-between text-[11px] font-bold text-muted">
          {ticks.map((p) => (
            <span key={p.day}>{tickLabel(p.day)}</span>
          ))}
        </div>
      </div>
    </>
  )
}

/**
 * The mini-curve under a stat tile.
 *
 * 🔴 IT DRAWS THE REAL SERIES OR IT DRAWS NOTHING. The four tiles on this
 * dashboard each carried a fixed `<path d="M0 20 Q25 15 50 8 T100 2">` — a
 * pleasant rise rendered under whatever number happened to be above it, so a
 * metric that had fallen still had an encouraging line beneath it. Two of the
 * four had no series behind them at all and now have no sparkline; these two do.
 *
 * ⚠️ NO AXIS, NO LABELS, NO ZERO BASELINE — and that is what a sparkline is. It
 * is shape beside a number, not a chart: the number above it is the value, and
 * this says only whether it has been climbing. Anything precise enough to read
 * off belongs in the chart above.
 */
export function Sparkline({
  points,
  seriesKey,
  stroke,
}: {
  points: readonly GrowthPoint[]
  seriesKey: SeriesKey
  stroke: string
}) {
  /* Fewer than two points is not a trend. A single dot stretched across the box
     would imply a flat month that was never measured. */
  if (points.length < 2) return null

  const values = points.map((p) => p[seriesKey])
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const step = 100 / (points.length - 1)

  const d = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(step * i).toFixed(1)},${(23 - ((v - min) / span) * 21).toFixed(1)}`)
    .join(' ')

  return (
    <svg className="mt-2 h-6 w-full" viewBox="0 0 100 25" fill="none" aria-hidden="true">
      <path d={d} stroke={stroke} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
